import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions } from '@material-ui/core';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';
import { useTranslation } from '@michelin/central-provider';
import { Button } from '@michelin/acid-components';
import { SelLocationsListTable } from './ListTable';
import { TechServiceAreaLocation } from '../types';
import { LocationAvatar } from '../Components/LocationAvatar';

interface PrimaryLocationReturn {
  isPrimary: true;
  primaryLocationInfo: TechServiceAreaLocation;
}
interface SecondaryLocationsReturn {
  isPrimary: false;
  secondaryLocations: string[];
  st: string;
}
interface SelLocationsModalProps {
  onClose: () => void;
  onUpdate: (data: PrimaryLocationReturn | SecondaryLocationsReturn) => void;
  bt: string;
  primarySt?: string;
  isPrimary: boolean;
  primaryLocations: string[];
  secondaryLocations: string[];
  primaryInfo: TechServiceAreaLocation;
}

function arraysHaveSameElements(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const sortedArr1 = [...arr1].sort();
  const sortedArr2 = [...arr2].sort();
  return sortedArr1.every((element, index) => element === sortedArr2[index]);
}

export function SelectLocationsModal(props: SelLocationsModalProps) {
  const {
    onClose,
    onUpdate,
    bt,
    primarySt,
    isPrimary,
    primaryLocations,
    secondaryLocations,
    primaryInfo,
  } = props;

  useEffect(() => {
    if ((!isPrimary && !primarySt) || (isPrimary && primarySt)) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [auxSecondaryLocations, setAuxSecondaryLocations] = useState<string[]>(secondaryLocations);
  const [secHasChanged, setSecHasChanged] = useState(false);
  useEffect(() => {
    const hasSameElements = arraysHaveSameElements(
      secondaryLocations,
      auxSecondaryLocations,
    );
    setSecHasChanged(!hasSameElements);
  }, [auxSecondaryLocations, secondaryLocations]);
  const onSelect = (data: { added: boolean; st: string }) => {
    const { added, st } = data;
    if (!added) {
      const newSecondaryLocations = auxSecondaryLocations.filter(
        (loc) => loc !== st,
      );
      setAuxSecondaryLocations(newSecondaryLocations);
    } else if (!auxSecondaryLocations.includes(st)) {
      setAuxSecondaryLocations([...auxSecondaryLocations, st]);
    }
  };

  const { t } = useTranslation();
  const title = isPrimary
    ? t('Select Primary ST Location')
    : t('Select Secondary ST Locations');

  return (
    <Dialog
      onClose={() => onClose?.()}
      aria-labelledby="customized-dialog-title"
      open
      maxWidth="md"
      scroll="paper"
      disableBackdropClick
    >
      <ClosableDialogTitle onClose={() => onClose?.()}>
        <>
          {!isPrimary && <LocationAvatar location={primaryInfo} />}
          {title}
        </>
      </ClosableDialogTitle>

      <ClosableDialogContent dividers>
        <SelLocationsListTable
          stNumber={primarySt}
          btNumber={bt}
          isPrimary={isPrimary}
          primaryLocations={primaryLocations}
          secondaryLocations={auxSecondaryLocations}
          onSelectPrim={(data) => onUpdate({ isPrimary: true, primaryLocationInfo: data })}
          onSelectSec={(data) => onSelect(data)}
        />
      </ClosableDialogContent>
      <DialogActions>
        {!isPrimary && (
          <Button
            onClick={() => onUpdate({
              isPrimary: false,
              secondaryLocations: auxSecondaryLocations,
              st: primarySt || '',
            })}
            size="small"
            disabled={!secHasChanged}
            data-testid="update-button"
          >
            {t('Update')}
          </Button>
        )}
        <Button
          onClick={() => onClose?.()}
          size="small"
          data-testid="close-button"
        >
          {!isPrimary ? t('Cancel') : t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
